<template>
    <!-- 指标统计卡：用于各面板顶部的关键数字展示，accent 决定主色调 -->
    <div class="stat card scanline" :class="`accent-${accent}`">
        <div class="stat-top">
            <span class="stat-label cap">{{ label }}</span>
            <span v-if="icon" class="stat-icon">{{ icon }}</span>
        </div>
        <!-- 数字会从 0 滚动到目标值；非数字（如 —）则直接显示 -->
        <div class="stat-value" :class="{ rolling }">{{ display }}</div>
        <div v-if="hint" class="stat-hint muted2">{{ hint }}</div>
        <div class="stat-glow"></div>
    </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from "vue";

const props = defineProps({
    label: { type: String, default: "" },
    value: { type: [String, Number], default: 0 },
    hint: { type: String, default: "" },
    icon: { type: String, default: "" },
    // accent 取值：cyan / violet / mint / amber / pink
    accent: { type: String, default: "cyan" },
});

// 当前显示的数字（滚动过程中的中间值）
const current = ref(0);
const rolling = ref(false);

// 目标是否为纯数字；不是的话（比如占位符 —）就直接透传
const isNumeric = computed(() => {
    const n = Number(props.value);
    return props.value !== "" && props.value !== null && !Number.isNaN(n);
});

const display = computed(() => (isNumeric.value ? Math.round(current.value) : props.value));

// 用 requestAnimationFrame 做缓动滚动，营造"数字跳动"的动感
let raf = 0;
function rollTo(target) {
    cancelAnimationFrame(raf);
    const start = current.value;
    const diff = target - start;
    if (diff === 0) return;
    const dur = 700;
    const t0 = performance.now();
    rolling.value = true;
    const tick = (now) => {
        const p = Math.min(1, (now - t0) / dur);
        // easeOutCubic
        const e = 1 - Math.pow(1 - p, 3);
        current.value = start + diff * e;
        if (p < 1) {
            raf = requestAnimationFrame(tick);
        } else {
            current.value = target;
            rolling.value = false;
        }
    };
    raf = requestAnimationFrame(tick);
}

watch(
    () => props.value,
    (v) => {
        if (isNumeric.value) rollTo(Number(v));
    }
);

onMounted(() => {
    if (isNumeric.value) rollTo(Number(props.value));
});
</script>

<style scoped>
.stat {
    position: relative;
    padding: 16px 18px;
    overflow: hidden;
    min-width: 0;
}

.stat-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

/* 滚动中的数字带发光，强调"数据在跳动" */
.stat-value.rolling {
    animation: num-glow .7s ease-out;
}

.stat-icon {
    font-size: 18px;
    animation: hover-float 5s ease-in-out infinite;
    opacity: .85;
}

.stat-value {
    margin-top: 8px;
    font-size: 30px;
    font-weight: 800;
    letter-spacing: .01em;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
}

.stat-hint {
    margin-top: 6px;
    font-size: 12px;
}

/* 背景光晕，按 accent 着色 */
.stat-glow {
    position: absolute;
    right: -40px;
    top: -40px;
    width: 130px;
    height: 130px;
    border-radius: 50%;
    filter: blur(34px);
    opacity: .4;
    pointer-events: none;
}

.accent-cyan .stat-value {
    color: #c5f4ff;
}

.accent-cyan .stat-glow {
    background: var(--c-cyan);
}

.accent-violet .stat-value {
    color: #d7ccff;
}

.accent-violet .stat-glow {
    background: var(--c-violet);
}

.accent-mint .stat-value {
    color: #b6ffe5;
}

.accent-mint .stat-glow {
    background: var(--c-mint);
}

.accent-amber .stat-value {
    color: #ffe0a6;
}

.accent-amber .stat-glow {
    background: var(--c-amber);
}

.accent-pink .stat-value {
    color: #ffc2d6;
}

.accent-pink .stat-glow {
    background: var(--c-pink);
}
</style>
